import { z } from 'zod';
import { CHANNEL_NAME_MAX } from '../constants.js';
import { messagePreviewSchema } from './message.js';
import { idSchema, isoTimestampSchema } from './primitives.js';
/**
 * `direct` is a 1:1 conversation and always has exactly two members.
 * `group` is a named conversation with two or more members.
 * `saved` is "Saved messages": each user's own channel, with them as its only
 * member - notes to self, messages kept for later, and files carried between
 * their own devices. Every account has exactly one, created on first use.
 */
export const channelTypeSchema = z.enum(['direct', 'group', 'saved']);
/**
 * A conversation.
 *
 * `name` is always present. For a `group` channel it is user-chosen; for a
 * `direct` channel the server derives it from the participants, so clients can
 * render any channel uniformly without special-casing the type.
 *
 * `memberIds` is the full membership. It is expected to stay small (chat
 * channels, not broadcast audiences); if a channel ever needs paginated
 * membership, that becomes a separate endpoint rather than a change here.
 */
export const channelSchema = z.object({
    id: idSchema,
    type: channelTypeSchema,
    name: z.string().trim().min(1).max(CHANNEL_NAME_MAX),
    /** At least one: a group outlives its other members leaving, and is deleted when empty. */
    memberIds: z.array(idSchema).min(1),
    createdAt: isoTimestampSchema,
});
/**
 * Fields a client supplies when opening a channel. The server assigns the id and
 * timestamp, and adds the requesting user to `memberIds` if absent.
 *
 * A `direct` channel takes exactly one other member and no name; a `group`
 * channel requires a name.
 */
export const createChannelInputSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('direct'),
        memberId: idSchema,
    }),
    z.object({
        type: z.literal('group'),
        name: z.string().trim().min(1).max(CHANNEL_NAME_MAX),
        memberIds: z.array(idSchema).min(1),
    }),
]);
/** `POST /api/v1/channels/:channelId/members`: any member of a group may add people. */
export const addMembersInputSchema = z.object({
    memberIds: z.array(idSchema).min(1).max(100),
});
/**
 * A channel as the conversation list shows it: the channel, plus what the
 * sidebar needs without opening it - the newest message this member can see,
 * and how many messages from other people arrived after their read cursor.
 *
 * Returned by `GET /api/v1/channels`. Live updates still arrive as plain
 * `Channel`s on `chat.channel`; the client keeps the two parts apart.
 */
export const channelOverviewSchema = channelSchema.extend({
    lastMessage: messagePreviewSchema.nullable(),
    unreadCount: z.number().int().nonnegative(),
});
/**
 * Query for `DELETE /api/v1/channels/:channelId`.
 *
 * `me` (the default) is the original behaviour: a direct chat disappears for the
 * caller only, a group is left. `everyone` deletes a DIRECT chat for both people
 * - the channel and its whole history - and needs the administrator's
 * `allowDeleteChatForEveryone` setting. "Saved messages" is never deleted;
 * either scope empties it.
 */
export const deleteChatQuerySchema = z.object({
    scope: z.enum(['me', 'everyone']).default('me'),
});
//# sourceMappingURL=channel.js.map